import React from 'react';
import {
  FiCheck,
  FiCompass,
  FiEdit3,
  FiFileText,
  FiMessageCircle,
  FiMic,
  FiPause,
  FiSearch,
  FiSend,
  FiSliders,
  FiTarget,
  FiUsers,
  FiXCircle,
} from 'react-icons/fi';

export function SlidePreview({ index, copy, Icon }) {
  if (index === 0) {
    return (
      <div className="js-tutorial-mini is-scout">
        <header><span><Icon /> {copy.previews.scout.label}</span><i /></header>
        <div className="js-tutorial-mini__jobs">
          {copy.previews.scout.roles.map(([role, company], jobIndex) => (
            <article key={role}>
              <div><strong>{role}</strong><span>{company}</span></div>
              <b>{[92, 86, 79][jobIndex]}% <small>{copy.previews.scout.match}</small></b>
            </article>
          ))}
        </div>
      </div>
    );
  }

  if (index === 1) {
    return (
      <div className="js-tutorial-mini is-application">
        <header><span>{copy.previews.application.label}</span><i /></header>
        <section>
          <span className="js-tutorial-mini__company-mark"><Icon /></span>
          <div><strong>{copy.previews.application.role}</strong><small>{copy.previews.application.company}</small></div>
        </section>
        <ol>
          {copy.previews.application.items.map((item, itemIndex) => (
            <li key={item} className={itemIndex > 0 ? 'is-complete' : ''}>
              <span>{itemIndex > 0 ? <FiCheck /> : itemIndex + 1}</span><b>{item}</b>
            </li>
          ))}
        </ol>
      </div>
    );
  }

  const tiles = [
    [FiTarget, copy.previews.system.goal, copy.previews.system.goalValue],
    [FiMessageCircle, copy.previews.system.coach, copy.previews.system.coachValue],
    [FiUsers, copy.previews.system.radar, copy.previews.system.radarValue],
    [FiFileText, copy.previews.system.learning, copy.previews.system.learningValue],
  ];
  return (
    <div className="js-tutorial-mini is-system">
      {tiles.map(([TileIcon, label, value], tileIndex) => (
        <article key={label}>
          <span><TileIcon /></span>
          <div><strong>{label}</strong><small>{value}</small>{tileIndex === 0 && <i><b /></i>}</div>
        </article>
      ))}
    </div>
  );
}

const OTHER_FEATURE_ICONS = {
  opportunities: FiSearch,
  application: FiFileText,
  careerCoach: FiMessageCircle,
  learningPathways: FiTarget,
  interview: FiMessageCircle,
  companyRadar: FiUsers,
};

export function OtherFeaturesPreview({ entries }) {
  return (
    <div className="js-tutorial-mini is-system is-other-features">
      {entries.map(([key, [label, value]]) => {
        const Icon = OTHER_FEATURE_ICONS[key] || FiTarget;
        return <article key={key}><span><Icon /></span><div><strong>{label}</strong><small>{value}</small></div></article>;
      })}
    </div>
  );
}

function MiniHeader({ Icon, label }) {
  return <header><span><Icon /> {label}</span><i /></header>;
}

function CoachOverviewPreview({ preview }) {
  return (
    <div className="js-tutorial-mini is-focused">
      <MiniHeader Icon={FiMessageCircle} label={preview.label} />
      <div className="js-tutorial-mini__focus-cards">
        <article><span><FiTarget /></span><div><small>{preview.focus}</small><strong>{preview.focusValue}</strong></div></article>
        <article><span><FiCheck /></span><div><small>{preview.goal}</small><strong>{preview.goalValue}</strong><i><b /></i></div></article>
      </div>
    </div>
  );
}

function CoachAskPreview({ preview }) {
  return (
    <div className="js-tutorial-mini is-focused">
      <MiniHeader Icon={FiMessageCircle} label={preview.label} />
      <div className="js-tutorial-mini__coach-chat">
        <p className="is-user">{preview.question}</p>
        <p className="is-coach">{preview.answer}</p>
        <div className="js-tutorial-mini__coach-context">
          {preview.knows.map((item) => <small key={item}><FiCheck /> {item}</small>)}
        </div>
        <strong className="js-tutorial-mini__coach-value">{preview.value}</strong>
      </div>
    </div>
  );
}

function CoachCvPreview({ preview }) {
  return (
    <div className="js-tutorial-mini is-focused">
      <MiniHeader Icon={FiFileText} label={preview.label} />
      <div className="js-tutorial-mini__coach-cv">
        <strong><FiFileText /> {preview.title}</strong>
        <ol>{preview.sections.map((section) => <li key={section}><FiCheck /> {section}</li>)}</ol>
        <p><FiCheck /> {preview.saved}</p>
      </div>
    </div>
  );
}

function CareerPathsPreview({ preview }) {
  return (
    <div className="js-tutorial-mini is-focused">
      <MiniHeader Icon={FiCompass} label={preview.label} />
      <div className="js-tutorial-mini__path-list">
        {preview.roles.map(([role, details]) => <article key={role}><span><FiCompass /></span><div><strong>{role}</strong><small>{details}</small></div></article>)}
      </div>
    </div>
  );
}

function ScoutEditPreview({ preview }) {
  return (
    <div className="js-tutorial-mini is-focused">
      <MiniHeader Icon={FiSliders} label={preview.label} />
      <div className="js-tutorial-mini__setting-list">
        {preview.items.map(([label, value]) => <article key={label}><div><small>{label}</small><strong>{value}</strong></div><FiEdit3 /></article>)}
      </div>
    </div>
  );
}

function CoachInterviewPreview({ preview }) {
  return (
    <div className="js-tutorial-mini is-focused">
      <MiniHeader Icon={FiMic} label={preview.label} />
      <div className="js-tutorial-mini__voice-guide">
        <blockquote>{preview.question}</blockquote>
        <i>{[1, 2, 3, 4, 5, 6, 7, 8].map((bar) => <b key={bar} />)}</i>
        <small><FiCheck /> {preview.result}</small>
      </div>
    </div>
  );
}

function InboxMessagesPreview({ preview }) {
  return (
    <div className="js-tutorial-mini is-focused">
      <MiniHeader Icon={FiMessageCircle} label={preview.label} />
      <div className="js-tutorial-mini__inbox-head"><strong>{preview.role}</strong><span>{preview.company} · {preview.recruiter}</span></div>
      <div className="js-tutorial-mini__message-thread">
        <p>{preview.messages[0]}</p><p className="is-user">{preview.messages[1]}</p>
      </div>
    </div>
  );
}

function InboxOutreachPreview({ preview }) {
  return (
    <div className="js-tutorial-mini is-focused">
      <MiniHeader Icon={FiSend} label={preview.label} />
      <div className="js-tutorial-mini__outreach-sequence">
        <strong>{preview.role}</strong><span>{preview.company} · {preview.contacts}</span>
        <ol><li><FiCheck /> {preview.sent}</li><li><FiSend /> {preview.next}</li></ol>
        <div><span><FiPause /> {preview.controls[0]}</span><span><FiXCircle /> {preview.controls[1]}</span></div>
      </div>
    </div>
  );
}

export function FocusedGuidePreview({ kind, copy, Icon }) {
  const preview = copy.previews[kind];
  if (kind === 'coachOverview') return <CoachOverviewPreview preview={preview} />;
  if (kind === 'coachAsk') return <CoachAskPreview preview={preview} />;
  if (kind === 'coachCv') return <CoachCvPreview preview={preview} />;
  if (kind === 'careerPaths') return <CareerPathsPreview preview={preview} />;
  if (kind === 'scoutEdit') return <ScoutEditPreview preview={preview} />;
  if (kind === 'coachInterview') return <CoachInterviewPreview preview={preview} />;
  if (kind === 'inboxMessages') return <InboxMessagesPreview preview={preview} />;
  if (kind === 'inboxOutreach') return <InboxOutreachPreview preview={preview} />;
  return <SlidePreview index={kind === 'application' ? 1 : 0} copy={copy} Icon={Icon} />;
}
